import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, GripVertical, RefreshCw, ImageIcon } from "lucide-react";
import { api, formatApiError } from "../api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const galleryUrl = (id) => `${BACKEND}/api/gallery/photos/${id}`;
const ACCEPT = ".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp";
const MAX = 24;

export function GalleryAdmin() {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragId, setDragId] = useState(null);
  const addRef = useRef(null);
  const replaceRef = useRef(null);
  const replaceTarget = useRef(null);

  const load = () =>
    api.get("/gallery").then((r) => setPhotos(r.data?.photos || [])).catch(() => {});

  useEffect(() => { load(); }, []);

  const addPhoto = async (fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    if (photos.length >= MAX) {
      toast.error(`Máximo ${MAX} fotografías.`);
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("alt", "");
    setUploading(true);
    try {
      const { data } = await api.post("/admin/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos(data.photos);
      toast.success("Fotografía agregada");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (addRef.current) addRef.current.value = "";
    }
  };

  const replacePhoto = async (fileList) => {
    const file = fileList?.[0];
    const gid = replaceTarget.current;
    if (!file || !gid) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/admin/gallery/${gid}/replace`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos(data.photos);
      toast.success("Fotografía reemplazada");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      replaceTarget.current = null;
      if (replaceRef.current) replaceRef.current.value = "";
    }
  };

  const remove = async (gid) => {    if (!window.confirm("¿Eliminar esta fotografía de la galería?")) return;
    try {
      const { data } = await api.delete(`/admin/gallery/${gid}`);
      setPhotos(data.photos);
      toast.success("Fotografía eliminada");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const saveCaption = async (gid, value) => {
    const alt = (value || "").trim();
    const current = photos.find((p) => p.id === gid);
    if (current && (current.alt || "") === alt) return;
    try {
      const { data } = await api.put(`/admin/gallery/${gid}/caption`, { alt });
      setPhotos(data.photos);
      toast.success("Pie de foto actualizado");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const persistOrder = async (list) => {
    try {
      const { data } = await api.put("/admin/gallery/order", { photo_ids: list.map((p) => p.id) });
      setPhotos(data.photos);
    } catch (err) {
      toast.error("No se pudo reordenar");
      load();
    }
  };

  const onDrop = (targetId) => {
    if (!dragId || dragId === targetId) return;
    const list = [...photos];
    const from = list.findIndex((p) => p.id === dragId);
    const to = list.findIndex((p) => p.id === targetId);
    const [moved] = list.splice(from, 1);
    list.splice(to, 0, moved);
    setPhotos(list);
    persistOrder(list);
    setDragId(null);
  };

  return (
    <div data-testid="admin-gallery">
      <h1 className="text-3xl font-bold text-black">Galería</h1>
      <p className="mt-2 text-sm font-light text-black/50">
        Administra las fotografías de la sección Galería de la landing. El orden determina su posición en el diseño.
        Arrastra las miniaturas para reordenar; los cambios se reflejan automáticamente en el sitio.
      </p>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <input ref={addRef} type="file" accept={ACCEPT} className="hidden" data-testid="gallery-file-input" onChange={(e) => addPhoto(e.target.files)} />
        <input ref={replaceRef} type="file" accept={ACCEPT} className="hidden" data-testid="gallery-replace-input" onChange={(e) => replacePhoto(e.target.files)} />
        <button
          onClick={() => addRef.current?.click()}
          disabled={uploading || photos.length >= MAX}
          data-testid="gallery-add-btn"
          className="inline-flex items-center gap-2 bg-[#7D5A44] text-white px-5 py-3 text-xs font-bold uppercase tracking-wide hover:bg-[#4A342A] transition-colors disabled:opacity-50"
        >
          <Upload size={16} /> {uploading ? "Subiendo..." : "Agregar fotografía"}
        </button>
        <span className="text-xs font-light text-[#B2967D]">{photos.length}/{MAX}</span>
      </div>

      <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {photos.map((p, i) => (
          <div
            key={p.id}
            data-testid={`gallery-thumb-${i}`}
            className="border border-[#B2967D]/30 bg-white"
          >
            <div
              draggable
              onDragStart={() => setDragId(p.id)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(p.id)}
              className="relative group cursor-move"
            >
              <img src={galleryUrl(p.id)} alt={p.alt || ""} className="w-full h-40 object-cover" />
              <span className="absolute top-2 left-2 inline-flex items-center gap-1 bg-[#7D5A44] text-white text-[10px] font-bold px-2 py-1">
                {i + 1}
              </span>
              <span className="absolute top-2 right-2 text-white/90 bg-black/30 p-1">
                <GripVertical size={12} />
              </span>
              <div className="absolute bottom-2 right-2 flex gap-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => { replaceTarget.current = p.id; replaceRef.current?.click(); }}
                  data-testid={`gallery-replace-${i}`}
                  title="Reemplazar"
                  className="bg-white/90 text-[#7D5A44] p-1.5 hover:bg-white"
                >
                  <RefreshCw size={14} />
                </button>
                <button
                  onClick={() => remove(p.id)}
                  data-testid={`gallery-delete-${i}`}
                  title="Eliminar"
                  className="bg-white/90 text-red-600 p-1.5 hover:bg-white"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            <input
              data-testid={`gallery-caption-${i}`}
              defaultValue={p.alt || ""}
              draggable={false}
              placeholder="Pie de foto (opcional)"
              onMouseDown={(e) => e.stopPropagation()}
              onBlur={(e) => saveCaption(p.id, e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
              className="w-full px-2 py-2 text-xs border-t border-[#B2967D]/30 focus:outline-none focus:bg-[#F5F1EA] text-black/70 placeholder:text-[#B2967D]"
            />
          </div>
        ))}
        {photos.length === 0 && (
          <div className="col-span-full text-center py-16 text-sm font-light text-black/40 border border-dashed border-[#B2967D]/40 flex flex-col items-center gap-2">
            <ImageIcon size={28} className="text-[#B2967D]" />
            Aún no hay fotografías en la galería.
          </div>
        )}
      </div>

      {photos.length > 0 && (
        <p className="mt-4 text-xs font-light text-[#B2967D]">
          Arrastra para reordenar. La posición 1 ocupa el bloque grande destacado del diseño de la galería.
        </p>
      )}
    </div>
  );
}
